import { API_BASE } from "./config";
import { bounceIfUnauthorized } from "./session";

/**
 * The Stats client — one call per tab (日本-337).
 *
 * **Per tab, not per panel.** One `graphs()` call on the server answers most of History and most of
 * Recall, so a request per panel would make N calls of it for one screen.
 *
 * Every request carries the deck. There is no "all decks": a statistic over the whole collection
 * would mix the Japanese vocabulary deck with whatever else happens to be in there, and the four
 * tabs would stop describing the same population as each other.
 */

export interface StatsDeck {
  name: string;
  /** Cards in this deck and its children, **suspended excluded** — the population the tabs
   *  describe, so the picker cannot advertise a number the screens do not use. */
  cardCount: number;
}

export interface StatsDecks {
  decks: StatsDeck[];
  /** Where to land when the URL names no deck. The server answers, because it is the one that
   *  knows the configured vocab deck. */
  default: string;
  /** The hour Anki's day rolls at. **Asked, not assumed** — the top bar used to state `4am` as a
   *  literal, and it is the one fact that reconciles "I studied that day" with a blank square: a
   *  review logged at 00:30 belongs to the day before. */
  rolloverHour: number;
}

export interface HeatmapDay {
  day: string;
  count: number;
  /** `-1` a missed day, `-2` today with no reviews yet. Neither is the palest shade: they are
   *  states of their own, and a day still in progress has not been missed at all. */
  bucket: number;
  /** What the hover card shows beside the count, off the same query as the shade — so the tooltip
   *  cannot describe a different day from the cell under the pointer. */
  young: number;
  mature: number;
  passed: number;
  seconds: number;
}

export interface HeatmapYear {
  year: number;
  days: HeatmapDay[];
  average: number;
  missed: number;
  perfect: boolean;
}

export interface Heatmap {
  years: HeatmapYear[];
  thresholds: number[];
  totalDays: number;
  studiedDays: number;
  missedDays: number;
  /** Studied against the days that have **finished** — an in-progress today is not in the
   *  denominator, or the figure would dip at midnight and recover by breakfast. */
  shareStudied: number;
  /** Whether the last cell is today with nothing in it yet. The legend names that state only when
   *  it is actually on screen. */
  inProgress: boolean;
}

export interface Streak {
  current: number;
  started: string | null;
  longest: number;
  longestEnded: string | null;
  daysStudied: number;
  daysMissed: number;
  totalReviews: number;
}

export interface LoadBar {
  day: string;
  offset: number;
  /** What the bar is drawn to. **Today is `reviewed + due`**, which is why the two are separate. */
  count: number;
  reviewed: number;
  due: number;
  /** What the FSRS simulation expects on this day — 日本-340. Zero on every past bar and on today.
   *  Already in the same unit as `count`: the server scales it by a measured presses-per-reviewed-
   *  card ratio, because `daily_review_count` counts cards answered in review state where every
   *  actual figure here counts button presses. */
  modelled: number;
  /** What the load would be if new cards kept arriving at the deck's new/day limit — 日本-346.
   *  The third mark, **uncapped** where `modelled` keeps the deck's review cap: the two answer
   *  different questions (what will be answered, against what the demand would be), and a curve
   *  simulated at the cap can never cross it.
   *
   *  Zero on the past and on today, and zero throughout on a deck that introduces nothing. Gate the
   *  legend key on `limits.newPerDay`, not on this — an all-zero series and an uncomputed one look
   *  identical from here, and only the first deserves a key. */
  atLimit: number;
  future: boolean;
}

export interface LoadAxis {
  bars: LoadBar[];
  /** **One maximum for both halves.** Scaling each half to its own peak would destroy the
   *  comparison this panel exists to make while still looking like a chart. */
  scaleMax: number;
  reviewLimit: number | null;
}

export interface LoadComparison {
  windowDays: number;
  aheadAverage: number;
  aheadPeak: number;
  /** The date the peak falls on — the same figure repeating down all three windows reads as a bug
   *  until you can see it is one day inside all three of them. */
  aheadPeakDay: string;
  behindAverage: number;
  behindPeak: number;
  behindPeakDay: string;
  /** Signed fraction; `null` when nothing is behind, which renders as "—" rather than ∞. */
  delta: number | null;
  /** The same three figures from the FSRS simulation — 日本-340, drawn beside the scheduled ones.
   *  **This is the panel that prompted the ticket**: `aheadAverage` over 365 days read 19/day
   *  against a habit of two to three hundred. */
  modelledAverage: number;
  modelledPeak: number;
  /** Carried for hover rather than printed as a forecast: one Monte Carlo trajectory puts a peak's
   *  *date* at a standard deviation of 37 days, where its aggregates are good to ~1%. */
  modelledPeakDay: string;
  /** The modelled forecast against the same window behind — **the sentence this panel exists to
   *  produce**, and the first version of it that can be true. `delta` compares a floor with a
   *  measurement. */
  modelledDelta: number | null;
  /** The keep-mining figures — 日本-346, the fifth column. `atLimitPeakDay` carries
   *  `modelledPeakDay`'s caveat: one Monte Carlo trajectory, so the aggregate is good to ~1% and
   *  the peak's date has a standard deviation of 37 days. */
  atLimitAverage: number;
  atLimitPeak: number;
  atLimitPeakDay: string;
  /** **Not a column.** The table already carries one Δ and the keep-mining pair has taken it to
   *  six columns; a second Δ would make seven, which does not fit at 1147. So this rides the
   *  panel's headline sentence as a second clause. */
  atLimitDelta: number | null;
}

/**
 * History's habit half on its own — `GET /api/stats/habit` (日本-718).
 *
 * The grid and the streak need one revlog scan; the rest of `/history` runs `graphs()` and two FSRS
 * simulations, ~5.7 s on production cold. The Review landing draws only these two, and History's
 * left column draws them before the forecast lands. `StatsHistory` extends this rather than
 * restating it, so a panel typed on this accepts either response.
 */
export interface StatsHabit {
  deck: string;
  today: string;
  rolloverHour: number;
  /** What the heatmap and streak cover — the chosen deck's parent, or `""` for the whole
   *  collection. Wider than the rest of the tab on purpose, so the panel names it. */
  habitDeck: string;
  heatmap: Heatmap;
  streak: Streak;
}

export interface StatsHistory extends StatsHabit {
  load: LoadAxis;
  comparison: LoadComparison[];
  backlog: {
    total: number;
    carried: number;
    /** Cards whose due date was today, answered or not — what the day actually asked of you. */
    becameDueToday: number;
    doneToday: number;
    carriedCleared: number;
  };
  limits: { newPerDay: number; reviewsPerDay: number };
  /** How much to believe the modelled curve. Every field is a state in which the simulation
   *  succeeds and quietly answers a different question, which is why they reach the panel rather
   *  than a log. */
  forecast: {
    fsrs: boolean;
    paramsPresent: boolean;
    pressRatio: number;
    pressRatioMeasured: boolean;
  };
  /**
   * New cards a day the deck can absorb before the review cap starts clipping — frame `8a`'s
   * fifth row, and one of the two that shipped missing (日本-337).
   *
   * `null` where the question has no answer: no review limit set, or no measured cost to divide
   * by. **Zero is a real answer**, not a missing one — a deck already at its cap.
   */
  capHeadroomNewCards: number | null;
  /**
   * When projected load first crosses the review cap, if the deck keeps taking new cards at its
   * configured limit — frame `8a`'s `First clipped day`.
   *
   * **Rendered as a month, never a date.** One Monte Carlo trajectory puts a peak's date at a
   * standard deviation of 37 days; even off the smoothed curve a month is as much precision as
   * this can carry. `null` means it does not clip inside the horizon, which is a real answer.
   */
  firstClippedDay: string | null;
  /** The rate the answer assumes — the deck's own new/day limit, not one we picked. */
  clippingRate: number;
  clippingHorizonDays: number;
  reviewsPerNewCard: number;
  /** False when the cost is the documented fallback rather than a measurement, which the row
   *  says out loud — the figure rests on it. */
  reviewsMeasured: boolean;
}

export interface CoverageRow {
  label: string;
  tag: string;
  learned: number;
  learning: number;
  new: number;
  /** `null` for a row with no denominator — `No rank` alone. Rendered hatched, with `—` for absent
   *  and cover, because a percentage of nothing is not zero. */
  universe: number | null;
  absent: number | null;
  cover: number | null;
}

export interface CoverageTrend {
  tag: string;
  label: string;
  points: { label: string; cover: number }[];
  universe: number | null;
  current: number | null;
  /** Terms per day into this tier over the trailing two years. */
  addRate: number;
  /** **`null` is never** — the honest answer for every tier but the top few, drawn in red. */
  completesOn: string | null;
}

export interface StatsCoverage {
  deck: string;
  tiers: CoverageRow[];
  tags: CoverageRow[];
  trends: CoverageTrend[];
  /** Notes with no enrichment tag, and so invisible to every bar. Never silently dropped. */
  unenriched: number;
  totalNotes: number;
}

export interface PaceRate {
  /** The stable handle — `lifetime`, `trailing-30`, `today`. **Find a rate by this, never by
   *  `label`**: the label is copy and is Japanese, and four call sites used to match on it. */
  id: string;
  label: string;
  perDay: number;
  /** The count behind the rate. No rate on these screens renders without its denominator. */
  cards: number;
  days: number;
}

export interface ProjectionRow {
  rate: number;
  /** `current`, `deck-limit`, or empty — what the highlight keys on. `note` beside it is copy. */
  kind: string;
  note: string;
  needsSettingsChange: boolean;
  /** `null` is **never** — at or below the goal the lifetime average never catches up. Render the
   *  word, not a blank: a blank reads as missing data. */
  reachesGoalOn: string | null;
  daysToGoal: number | null;
  /** The demand — cards LEARNED on the way to the goal. The panel prints it once; the table draws
   *  `cardsToMine`, since the two differ by a constant on every row. */
  cardsToAdd: number | null;
  /** What must be MINED: the demand minus the unseen cards already in the deck. `null` is **never**
   *  and renders as the word — a `0` there would read as *nothing needed*. */
  cardsToMine: number | null;
  minePerDay: number | null;
  addedReviewLoad: number;
  totalReviewLoad: number;
}

/** Is there anything left in the deck to learn between here and the goal (日本-461). */
export interface Supply {
  unseen: number;
  /** The deck's new-per-day **limit** — what the panel says on its face. Deliberately not the
   *  trailing-30 rate the tab calls *current*; see `stats/pace.py`. */
  drainPerDay: number;
  runwayDays: number | null;
  daysToGoal: number | null;
  reachesGoalOn: string | null;
  demand: number | null;
  cardsToMine: number | null;
  minePerDay: number | null;
}

export interface StatsSettings {
  goalPerDay: number;
  fsrsTarget: number;
  paceStartDate: string;
  /** The deck last chosen in the picker; empty means never chosen. */
  deck: string;
}

export interface StatsPace {
  deck: string;
  today: string;
  start: string;
  settings: StatsSettings;
  rates: PaceRate[];
  gap: {
    goal: number;
    cardsHeld: number;
    daysElapsed: number;
    expectedByNow: number;
    cardsBehind: number;
    gapGrowthPerDay: number;
    gapGrowthPerYear: number;
  };
  projection: ProjectionRow[];
  reviewsPerNewCard: number;
  /** False when the collection has too little history and the figure is a stated fallback. The
   *  panel must say which — an assumption dressed as a measurement is what changes a decision. */
  reviewsMeasured: boolean;
  currentReviewLoad: number;
  unseen: number;
  supply: Supply;
  newCardLimit: number;
}

export interface RateRequired {
  by: string;
  rate: number | null;
  multipleOfCurrent: number | null;
  aboveDeckLimit: number | null;
  addedReviewLoad: number | null;
  cardsToAdd: number | null;
  cardsToMine: number | null;
}

export interface RetentionCell {
  passed: number;
  failed: number;
  total: number;
  /** `null`, never 0, when nothing was reviewed — the state every "today" cell is in each morning. */
  rate: number | null;
  /** Below the small-sample threshold: the baseline comparison is de-emphasised so that 12 reviews
   *  at 75% cannot read as a trend. */
  thin: boolean;
}

export interface RetentionRow {
  period: string;
  label: string;
  young: RetentionCell;
  mature: RetentionCell;
  all: RetentionCell;
}

export interface ButtonRow {
  label: string;
  again: number;
  hard: number;
  good: number;
  easy: number;
  total: number;
  rate: number | null;
}

export interface Bucket {
  label: string;
  count: number;
  /** The bin's lower bound in Anki's units, so a caller can colour by band. */
  low: number;
}

export interface Distribution {
  /** **Binned** — Anki hands back one entry per distinct value, which is 927 stability bars. */
  buckets: Bucket[];
  average: number;
  median: number;
  /** Share in the top band — the design's `57% of cards above 90`. */
  shareHigh: number;
}

export interface StatsRecall {
  deck: string;
  /** With FSRS off there is no difficulty, stability or retrievability — the panels say so rather
   *  than drawing zeroes. A **collection** flag, not a deck one. */
  fsrs: boolean;
  /** The deck's own preset carries FSRS parameters. `true` here with `fsrs` false is the shape a
   *  restored collection has — the preset came across in the `.apkg`, the switch did not — and it
   *  is worth saying rather than reporting a flat "FSRS is off". */
  fsrsConfigured: boolean;
  /** What FSRS is aiming at, off the deck preset. Distinct from `target`, which is the line Alex
   *  judges retention against. */
  desiredRetention: number | null;
  target: number;
  retention: RetentionRow[];
  today: {
    rate: number | null;
    reviews: number;
    thin: boolean;
    baselines: [string, number | null][];
  };
  buttons: ButtonRow[];
  hours: { hour: number; reviews: number; rate: number | null }[];
  lapses: { label: string; count: number; rate: number | null }[];
  difficulty: Distribution;
  stability: Distribution;
  retrievability: Distribution;
  leeches: number;
}

export interface TrendPoint {
  day: string;
  /** `null` is a **gap** — a window with too few reviews to have a rate. Break the line there;
   *  drawing it to the floor reads as a month of total failure. */
  young: number | null;
  mature: number | null;
  all: number | null;
}

export interface DifficultyBand {
  label: string;
  low: number;
  reviews: number;
  passed: number;
  /** `null`, never 0, on a band with nothing in it. */
  rate: number | null;
}

export interface StatsTrend {
  deck: string;
  windowDays: number;
  points: TrendPoint[];
  /** Pass rate by the card's current difficulty. **On this route rather than `/recall`** because it
   *  is the same kind of read — a revlog scan on the batch handle — so the two arrive together and
   *  the fast tab does not wait for either. */
  difficultyBands: DifficultyBand[];
}

export interface CalibrationBand {
  low: number;
  predicted: number;
  /** `null` below the review threshold — no rate, rather than a zero somebody would conclude from. */
  actual: number | null;
  gap: number | null;
  count: number;
}

export interface StatsCalibration {
  deck: string;
  /** False means the job has **never run**. Distinct from a run that scored nothing: the first is
   *  fixed by scheduling a job, the second by turning FSRS on. */
  computed: boolean;
  computedAt: number | null;
  fsrsParams: string;
  bands: CalibrationBand[];
  reviewsScored: number;
  cardsScored: number;
  cardsSkipped: number;
  meanGap: number | null;
  bandsBelow: number;
  bandsMeasured: number;
  actualAtTarget: number | null;
  impliedIntervalMultiplier: number | null;
  target: number;
  jobId: string;
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { credentials: "include" });
  if (!res.ok) {
    bounceIfUnauthorized(res);
    throw new Error(`Request failed (${res.status})`);
  }
  return (await res.json()) as T;
}

export function statsDecks(): Promise<StatsDecks> {
  return get<StatsDecks>("/api/stats/decks");
}

export function statsHabit(deck: string): Promise<StatsHabit> {
  return get<StatsHabit>(`/api/stats/habit?deck=${encodeURIComponent(deck)}`);
}

export function statsHistory(deck: string): Promise<StatsHistory> {
  return get<StatsHistory>(`/api/stats/history?deck=${encodeURIComponent(deck)}`);
}

export function statsCoverage(deck: string): Promise<StatsCoverage> {
  return get<StatsCoverage>(`/api/stats/coverage?deck=${encodeURIComponent(deck)}`);
}

export function statsRecall(deck: string): Promise<StatsRecall> {
  return get<StatsRecall>(`/api/stats/recall?deck=${encodeURIComponent(deck)}`);
}

/**
 * `Predicted recall`'s two figures, on their own route.
 *
 * Split off the tab because they are the only per-card backend work outside the nightly job — 1,393
 * `card_stats_data` calls and 3.2s, measured, in front of eleven panels that are one `graphs()` call
 * and arithmetic. The panel draws its heading, its target button and its list rows immediately and
 * skeletons these two numbers.
 */
export interface StatsPredicted {
  deck: string;
  dueToday: number | null;
  dueWeek: number | null;
}

export function statsPredicted(deck: string): Promise<StatsPredicted> {
  return get<StatsPredicted>(`/api/stats/recall/predicted?deck=${encodeURIComponent(deck)}`);
}

export function statsTrend(deck: string): Promise<StatsTrend> {
  return get<StatsTrend>(`/api/stats/trend?deck=${encodeURIComponent(deck)}`);
}

export function statsCalibration(deck: string): Promise<StatsCalibration> {
  return get<StatsCalibration>(`/api/stats/calibration?deck=${encodeURIComponent(deck)}`);
}

export function statsPace(deck: string): Promise<StatsPace> {
  return get<StatsPace>(`/api/stats/pace?deck=${encodeURIComponent(deck)}`);
}

export function rateRequiredBy(deck: string, by: string): Promise<RateRequired> {
  return get<RateRequired>(
    `/api/stats/pace/required?deck=${encodeURIComponent(deck)}&by=${encodeURIComponent(by)}`,
  );
}

export async function saveStatsSettings(
  patch: Partial<StatsSettings>,
): Promise<StatsSettings> {
  const res = await fetch(`${API_BASE}/api/stats/settings`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    bounceIfUnauthorized(res);
    // Bounds refuse rather than clamp on the server, so the message is worth surfacing verbatim —
    // it names the range, which a generic failure would not.
    const body = await res.json().catch(() => null);
    throw new Error(typeof body?.detail === "string" ? body.detail : `Request failed (${res.status})`);
  }
  return (await res.json()) as StatsSettings;
}

export interface ForecastAccuracy {
  days: number;
  /** Mean absolute error in reviews a day. `null` below the threshold — a rate with too little
   *  behind it is not a rate. */
  meanError: number | null;
  /** Signed: **negative forecast too little.** Beside the magnitude because `future_due` is
   *  negative by construction, and a model erring the other way would look better on magnitude
   *  alone while being no more useful. */
  bias: number | null;
}

export interface StatsBacktest {
  deck: string;
  /** Forecasts recorded at all, scoreable or not. With `modelled.days` this is what tells the three
   *  empty states apart, and each sends you somewhere different. */
  snapshots: number;
  scoredDays: number;
  modelled: ForecastAccuracy;
  scheduled: ForecastAccuracy;
  /** `null` while either side is unmeasured — never `false`, which would read as the model having
   *  lost a comparison that was never run. */
  modelWins: boolean | null;
}

export function statsBacktest(deck: string): Promise<StatsBacktest> {
  return get<StatsBacktest>(`/api/stats/backtest?deck=${encodeURIComponent(deck)}`);
}
